import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Building2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { authClient } from '@/lib/auth-client';

export const Route = createFileRoute('/_authenticated/_organization/organization/select')({
  component: SelectOrganization,
});

interface Organization {
  id: string;
  name: string;
  slug: string;
  createdAt: Date;
}

function SelectOrganization() {
  const [isSelecting, setIsSelecting] = useState(false);
  const navigate = useNavigate();

  const { data: organizations, isPending: isLoading, error } = authClient.useListOrganizations();

  useEffect(() => {
    if (error) {
      toast.error(error.message || 'Failed to load organizations');
      navigate({ to: '/' });
    }
  }, [error, navigate]);

  const handleSelectOrganization = async (organization: Organization) => {
    setIsSelecting(true);

    try {
      await authClient.organization.setActive({
        organizationId: organization.id,
        organizationSlug: organization.slug,
      });

      toast.success(`Switched to ${organization.name}`);

      navigate({ to: '/' });
    } catch {
      toast.error('Failed to select organization');
    } finally {
      setIsSelecting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-2xl py-8">
        <div className="space-y-4">
          <div className="h-8 bg-gray-200 rounded animate-pulse" />
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-2xl py-8">
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight">Select Organization</h1>
          <p className="text-muted-foreground mt-2">
            Choose which organization you'd like to work with
          </p>
        </div>

        <div className="space-y-2">
          {organizations?.map((organization) => (
            <div
              key={organization.id}
              className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
              onClick={() => handleSelectOrganization(organization)}
            >
              <div className="flex items-center space-x-3">
                <div className="bg-primary text-primary-foreground flex size-10 items-center justify-center rounded-md">
                  <Building2 className="size-5" />
                </div>
                <div>
                  <h3 className="font-medium">{organization.name}</h3>
                  <p className="text-sm text-muted-foreground">@{organization.slug}</p>
                </div>
              </div>
              <Button
                disabled={isSelecting}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectOrganization(organization);
                }}
              >
                {isSelecting ? 'Selecting...' : 'Select'}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
